import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, Modal, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getPublicationAddNewAliasSchema } from '@/module/Author/Publication/model/PublicationSchema';
import { PublicationNewAliasRequest, ReclaimablePublication } from '@/module/Author/Publication/model/PublicationTypes';

import { AsyncSelectControl, NumberInputControl, TextInputControl } from '@/core/components';

import { Nomenclature } from '@feature/nomenclature';
import { useCurrentUser } from '@feature/security';

export interface PublicationAddNewAliasModalProps {
  publication: ReclaimablePublication;
  memberships: Nomenclature[];
  membershipsLoading: boolean;
  membershipsError: boolean;
  onSubmit: (data: PublicationNewAliasRequest) => void;
}

export const PublicationAddNewAliasModal = ({
  publication,
  memberships,
  membershipsLoading,
  membershipsError,
  onSubmit,
}: PublicationAddNewAliasModalProps) => {
  const { email: alias, lastName, firstName } = useCurrentUser();

  const [opened, handlers] = useDisclosure(false);

  const { control, handleSubmit } = useForm<PublicationNewAliasRequest>({
    resolver: zodResolver(getPublicationAddNewAliasSchema(publication.authors.length)),
    defaultValues: {
      alias,
      userName: `${lastName} ${firstName}`,
    },
  });

  const { t } = useTranslation();

  return (
    <>
      <Button onClick={handlers.open}>{t('publications.addNewAlias')}</Button>
      <Modal
        centered
        opened={opened}
        onClose={handlers.close}
        size="lg"
        title={t('publications.addNewAlias')}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex
            gap="10px"
            direction="column"
          >
            <Flex gap="10px">
              <TextInputControl
                name="alias"
                label={t('authors.form.fields.alias')}
                control={control}
              />
              <TextInputControl
                name="userName"
                label={t('authors.form.fields.userName')}
                control={control}
              />
            </Flex>
            <Flex gap="10px">
              <AsyncSelectControl
                name="membership"
                label={t('authors.form.fields.membership')}
                control={control}
                options={memberships ?? []}
                isLoading={membershipsLoading}
                isError={membershipsError}
              />
              <NumberInputControl
                name="order"
                label={t('authors.form.fields.order')}
                control={control}
              />
            </Flex>
          </Flex>
          <Stack mt="20px">
            <Button
              loading={false}
              type="submit"
            >
              {t('button.add')}
            </Button>
          </Stack>
        </form>
      </Modal>
    </>
  );
};
